import { InputText, Select } from '@/components';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import React, { Fragment, JSX } from 'react';
import { OptionProps } from '@/components/Select';
import { IconWarning } from '@/assets/icons/IconWarning';
import { FormInputNameCreditCard } from '../types';

interface StateProps {
  formData: FormData;
  formErrors: FormErrors;
  validating3DS: boolean;
}

interface DispatchProps {
  onChangeFormInput: OnChangeFormInput;
}

type Props = StateProps & DispatchProps;

export const CreditCardContent: React.FC<Props> = ({
  validating3DS,
  formData,
  onChangeFormInput,
  formErrors,
}): JSX.Element => {
  const monthOptions: OptionProps[] = [
    { value: '01', label: '01' },
    { value: '02', label: '02' },
    { value: '03', label: '03' },
    { value: '04', label: '04' },
    { value: '05', label: '05' },
    { value: '06', label: '06' },
    { value: '07', label: '07' },
    { value: '08', label: '08' },
    { value: '09', label: '09' },
    { value: '10', label: '10' },
    { value: '11', label: '11' },
    { value: '12', label: '12' },
  ];

  const yearOptions: OptionProps[] = [
    { value: '2026', label: '26' },
    { value: '2027', label: '27' },
    { value: '2028', label: '28' },
    { value: '2029', label: '29' },
    { value: '2030', label: '30' },
    { value: '2031', label: '31' },
    { value: '2032', label: '32' },
    { value: '2033', label: '33' },
    { value: '2034', label: '34' },
    { value: '2035', label: '35' },
    { value: '2036', label: '36' },
    { value: '2037', label: '37' },
    { value: '2038', label: '38' },
    { value: '2039', label: '39' },
    { value: '2040', label: '40' },
    { value: '2041', label: '41' },
    { value: '2042', label: '42' },
    { value: '2043', label: '43' },
    { value: '2044', label: '44' },
    { value: '2045', label: '45' },
    { value: '2046', label: '46' },
    { value: '2047', label: '47' },
    { value: '2048', label: '48' },
    { value: '2049', label: '49' },
    { value: '2050', label: '50' },
  ];
  return (
    <Fragment>
      {validating3DS ? (
        <Fragment>
          <h2 className="font-dmsans font-normal text-neutro-b-400 text-[28px]">
            Cartão de Crédito
          </h2>
          <p className="font-dmsans text-base font-normal text-neutro-w-600 leading-[28px]">
            Vamos iniciar a validação do seu cartão.
          </p>
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <IconWarning height="30px" className="self-center text-alert-warning" />
            <div className="ml-[20px] text-left">
              <p className="text-sm">Aguardando análise do cartão de crédito</p>
            </div>
          </div>
        </Fragment>
      ) : (
        <Fragment>
          <h2 className="font-dmsans font-normal text-neutro-b-400 text-[28px]">
            Cartão de Crédito
          </h2>
          <p className="font-dmsans text-base font-normal text-neutro-w-600 leading-[28px]">
            Informe os dados do cartão com atenção: número, validade, CVV, nome e CPF do titular.
          </p>
          <div className="md:w-[500px] mt-[20px]" id="creditCardInformation">
            <div className="mb-[40px]">
              <InputText
                name="number"
                label="Número do cartão"
                placeholder="1234 5678 90123 3456"
                className={`appearance-none w-full
                      rounded-md text-heading leading-tight focus:outline-none
                      focus:shadow-outline text-xs font-dmsans font-normal`}
                value={formData[FormInputNameCreditCard.number]}
                onChange={e => onChangeFormInput(FormInputNameCreditCard.number)(e.target.value)}
                error={formErrors.number && formErrors.number[0]}
              />
            </div>
            <div className="mb-5 grid grid-cols-12 gap-4">
              <Select
                name="month"
                label="Mês"
                placeholder="Selecione..."
                value={formData[FormInputNameCreditCard.month]}
                onChange={e =>
                  onChangeFormInput(FormInputNameCreditCard.month)(e?.target.value as string)
                }
                className="col-span-4"
                error={formErrors.month && formErrors.month[0]}
                options={monthOptions}
              />

              <Select
                name="year"
                label="Ano"
                placeholder="Selecione..."
                value={formData[FormInputNameCreditCard.year]}
                onChange={e =>
                  onChangeFormInput(FormInputNameCreditCard.year)(e?.target.value as string)
                }
                className="col-span-4"
                error={formErrors.year && formErrors.year[0]}
                options={yearOptions}
              />

              <InputText
                name="cvv"
                label="CVV"
                placeholder="000"
                className="col-span-4 appearance-none w-full
              rounded-md text-heading leading-tight focus:outline-none
              focus:shadow-outline text-xs font-dmsans font-normal"
                value={formData[FormInputNameCreditCard.cvv]}
                onChange={e => onChangeFormInput(FormInputNameCreditCard.cvv)(e.target.value)}
                error={formErrors.cvv && formErrors.cvv[0]}
              />
            </div>
            <div className="mb-[20px]">
              <InputText
                name="name"
                label="Nome do títular"
                placeholder="Maria da Silva"
                className={`appearance-none w-full
                      rounded-md text-heading leading-tight focus:outline-none
                      focus:shadow-outline text-xs font-dmsans font-normal`}
                value={formData[FormInputNameCreditCard.name]}
                onChange={e => onChangeFormInput(FormInputNameCreditCard.name)(e.target.value)}
                error={formErrors.name && formErrors.name[0]}
              />
            </div>
            <div className="mb-[20px]">
              <InputText
                name="document"
                label="CPF do títular"
                placeholder="123.456.789-00"
                className={`appearance-none w-full
                      rounded-md text-heading leading-tight focus:outline-none
                      focus:shadow-outline text-xs font-dmsans font-normal`}
                maxLength={14}
                value={formData[FormInputNameCreditCard.document]}
                onChange={e => onChangeFormInput(FormInputNameCreditCard.document)(e.target.value)}
                error={formErrors.document && formErrors.document[0]}
              />
            </div>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};
